import Backbone from 'backbone'
import $ from 'jquery'
import _ from 'lodash'

import FormView from 'scoreboard/components/form-view'
import Dashboard from 'scoreboard/dashboards/dashboard'
import { createWobs } from 'scoreboard/dashboards/display/wobs'
import { secureWidgetTypes, DASHBOARD_WIDGET_TYPES } from 'scoreboard/dashboards/edit/dashboards-edit'
import Toolbar from 'scoreboard/dashboards/edit/dashboard-toolbar'
import EditableCanvasContainerView from 'scoreboard/dashboards/edit/editable-canvas-container-view'
import scrollNotifierTemplate from 'scoreboard/dashboards/edit/scroll-notifier.hbs'
import Maestro from 'scoreboard/dashboards/widgets/maestro'

import getEditorTitle from './get-editor-title'
import { MIN_HEIGHT, MIN_WIDTH } from './dashboard-widget-dimensions'
import template from './dashboard-widget-editor.hbs'
import calendars from 'scoreboard/components/calendars'
import addFirstHint from 'scoreboard/components/add-first-hint.hbs'
import i18n from 'scoreboard/components/i18n'

/**
 * Editor dialog for a dashboard form widget
 */
const DashboardWidgetEditor = Backbone.View.extend({
	tagName: 'form',

	className: 'form-widget-editor dashboard-widget-editor edit ' +
		'dashboard-background-white display-layout-dashboards',

	/**
	 * @type {Form}
 	 */
	model: null,

	/**
	 * @type {Dashboard}
	 */
	dashboard: null,

	/**
	 * Dialog for editing a Dashboard form widget
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model
	 *
	 * @param {FormWidget} options.widget the data for the widget
	 */
	initialize (options = {}) {
		this.options = options

		// Clone so that we don't mutate the original, in case the user cancels
		this.dashboard = new Dashboard(_.cloneDeep(options.widget.dashboard))
		if (options.widget.dashboardData) {
			this.dashboard.data.set(_.cloneDeep(options.widget.dashboardData))
		}

		this.formView = new FormView({
			el: this.el,
			submit: () => this.done()
		})

		this.maestro = new Maestro({
			data: this.dashboard.data,
			getPeriodId: () => this.getPeriodId()
		})

		this.wobs = createWobs({
			dashboardModel: this.dashboard,
			organizationId: this.model.get('organizationId'),
			getPeriodId: () => this.getPeriodId()
		})

		this.formWidget = new Backbone.Model(this.options.widget)
		this.listenTo(this.formWidget, 'change', () => {
			this.formView.setDirty()
		})
	},

	getPeriodId () {
		// #56207 dashboards in a form always use the default period,
		// because there isn't a calendar period selector
		return calendars.defaultPeriodId()
	},

	render () {
		this.$el.html(template(this.context()))

		this.renderToolbar()
		this.renderCanvasContainer()
		this.maxDimensions = true // Fill the window

		this.dashboard.data.fetch({
			calendarPeriodId: this.getPeriodId()
		})
		this.$('.canvas').append(addFirstHint({hint: i18n.get('dashboards.addFirstWidget')}))
		// Append the auto-expand helper message
		// #60418 - include wrapper
		let autoExpandMessageHtml =
			`<div class="dashboard-auto-expand-wrapper">
				<span class="dashboard-auto-expand-message">
					${i18n.get('dashboards.autoExpandMessage')}
				</span>
			</div>`
		this.$('.editable-canvas').prepend(autoExpandMessageHtml)

		this.formView.start()
	},

	context () {
		return {
			title: getEditorTitle(this.options.widget),
		}
	},

	renderToolbar () {
		this.toolbar = new Toolbar({
			model: this.dashboard,
			widgetTypes: secureWidgetTypes(DASHBOARD_WIDGET_TYPES),
			form: this.model,
			formWidget: this.formWidget,
			getPeriodId: () => this.getPeriodId()
		})

		this.$('.dashboard-toolbar').append(this.toolbar.el)
		this.toolbar.render()
	},

	renderCanvasContainer () {
		// Append the triangle in the bottom right corner of the canvas area
		let scrollNotifier = $(scrollNotifierTemplate())
		this.$('.dialog-body-content').append(scrollNotifier)

		this.canvasContainer = new EditableCanvasContainerView({
			model: this.dashboard,
			wobs: this.wobs,
			maestro: this.maestro,
			toolbar: this.toolbar,
			formView: this.formView,
			scrollNotifier,
			minHeight: MIN_HEIGHT,
			minWidth: MIN_WIDTH
		})
		this.$('.dialog-body-content').append(this.canvasContainer.el)
		this.canvasContainer.render()
	},

	done () {
		// Need to fetch fresh dashboard data because each widget might have
		// its own smaller portion of just the dashboard data it needs itself.
		// A fresh fetch will get the full dashboard data so it can be used
		// when the dashboard is re-rendered inside the form widget.
		this.dashboard.data.fetch({ calendarPeriodId: this.getPeriodId() }).then(() => {
			let dashboard = this.canvasContainer.getSaveData()
			dashboard.id = this.dashboard.get('id')
			dashboard.dirty = true

			let formWidget = _.extend({},
				this.options.widget,
				{
					dashboard,
					// Need to stick this on the widget so it's available when
					// the dashboard re-renders
					dashboardData: this.dashboard.data
				},
				this.formWidget.pick(
					'state', 'filterSourceWidgetId', 'filterSourceWidgetTempId'))

			this.trigger('done', formWidget)
		})
	},

	autoRemove: true
})

export default DashboardWidgetEditor

DashboardWidgetEditor.__filename__ = 'forms/widgets/dashboard-widget-editor.js'
